import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ISelectMenuOption } from "../../blent-component/Select/interfaces";
import { Icon } from "./../../blent-component/Icon";
import { LabelButton } from "./../../blent-component/Button";
import {
  JobCardViewerProps,
  GradientViewerProps,
  GradientType,
} from "./../../types/globals.types";
import styles from "./styles.module.scss";

const FooterSentence = ({ slogan }: { slogan: ISelectMenuOption[] }) => {
  const sloganType = !!slogan?.length ? slogan[0].value : "";
  switch (sloganType) {
    case "build-a-legacy":
      return (
        <div className={styles.buildLegacySlogan}>
          <div className={styles.SouthCoastFontFamily}>Build a Legacy</div>
          <div className={styles.atBacardi}>at bacardi</div>
        </div>
      );
    case "unleash-your-potential":
      return (
        <div className={styles.unleashPotentialSlogan}>
          <div className={styles.unleash}>Unleash your Potential</div>
          <div className={styles.atBacardi}>at bacardi</div>
        </div>
      );
    case "family-owned":
    default:
      return (
        <div className={styles.familyOwnedSlogan}>
          <div className={styles.owned}>Family-Owned</div>
          <div className={styles.since}>Since 1862</div>
        </div>
      );
  }
};

export const CardViewer: React.FC<JobCardViewerProps & GradientViewerProps> = ({
  job,
  img,
  gradient,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState(127);
  const [jobFunctionName, setJobFunctionName] = useState<string>();
  const [jobLocationName, setJobLocationName] = useState<string>();
  const [jobSeniorityName, setJobSeniorityName] = useState<string>();
  const [jobWorkSetUpName, setJobWorkSetUpName] = useState<string>();
  const [scale, setScale] = useState(1);

  const divRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const getThemeClass = (g: GradientType) => {
    switch (g) {
      case "pink":
        return styles.themePink;
      case "purple":
        return styles.themePurple;
      case "blue":
        return styles.themeBlue;
      case "green":
        return styles.themeGreen;
      case "yellow":
      default:
        return styles.themeYellow;
    }
  };
  const getNowHiringImage = (g: GradientType) => {
    switch (g) {
      case "blue":
        return "/img/NOW-HIRING-STICKER-BLUE.png";
      case "green":
        return "/img/NOW-HIRING-STICKER-TEAL.png";
      case "yellow":
        return "/img/NOW-HIRING-STICKER-ORANGE.png";
      case "pink":
        return "/img/NOW-HIRING-STICKER-PINK.png";
      case "purple":
      default:
        return "/img/NOW-HIRING-STICKER-BLUE.png";
    }
  };
  const jobThemeClass = getThemeClass(gradient);

  const loadImages = (element: HTMLElement): Promise<void[]> => {
    const images = Array.from(element.getElementsByTagName("img"));
    const promises = images.map((img) => {
      return new Promise<void>((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // handle image errors if necessary
        }
      });
    });
    return Promise.all(promises);
  };

  // Download the content as an image
  const downloadAsImage = () => {
    if (divRef.current) {
      loadImages(divRef.current).then(() => {
        html2canvas(divRef.current!, {
          useCORS: true,
          //
          // x: -50, // Décalage pour ajuster la capture du contenu rotatif
          // y: -50, // Ajuster en fonction de la taille de l'image
          // width: divRef.current!.offsetWidth + 100, // Augmenter la largeur pour inclure les ombres
          // height: divRef.current!.offsetHeight + 100, // Augmenter la hauteur pour inclure les ombres
          //
          scale: 2, // Améliore la résolution
          // backgroundColor: null, // Conserve la transparence
          // logging: true, // Pour aider à déboguer si nécessaire
          // imageTimeout: 15000, // Temps d'attente plus long pour le chargement des images
          // allowTaint: false,
        }).then((canvas) => {
          const link = document.createElement("a");
          link.href = canvas.toDataURL("image/jpeg");
          link.download = job?.jobTitle
            ? `linked-card-${job.jobTitle}.jpg`
            : "linked-card.jpg";
          link.click();
        });
      });
    }
  };

  // Download the content as a PDF
  const downloadAsPDF = () => {
    if (divRef.current) {
      loadImages(divRef.current).then(() => {
        html2canvas(divRef.current!, { useCORS: true }).then((canvas) => {
          const imgData = canvas.toDataURL("image/jpeg");

          // Dimensions du canvas
          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;

          // Taille du PDF en mm (A4 standard: 210x297mm)
          const pdfWidth = 210;
          let pdfHeight = (canvasHeight * pdfWidth) / canvasWidth; // Hauteur proportionnelle en mm

          // Si la hauteur calculée dépasse la hauteur maximale de la page A4
          if (pdfHeight > 297) {
            // Redimensionnez en conséquence pour que l'image s'adapte à la page
            pdfHeight = 297;
          }

          const fileName = job?.jobTitle
            ? `linked-card-${job.jobTitle}.pdf`
            : "linked-card.pdf";

          // Créer le PDF avec une hauteur ajustée
          const pdf = new jsPDF({
            orientation: "landscape",
            unit: "mm",
            format: [pdfWidth, pdfHeight],
          });

          // Ajouter l'image à la page PDF
          pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
          pdf.save(fileName);
        });
      });
    }
  };

  useEffect(() => {
    const _jobFunctionName = !!job.jobFunctionFromList?.length
      ? job.jobFunctionFromList[0].label
      : job.jobFunctionManually?.length > 0
      ? job.jobFunctionManually
      : "";
    setJobFunctionName(_jobFunctionName);
  }, [job.jobFunctionFromList, job.jobFunctionManually]);

  useEffect(() => {
    const _jobLocationName = !!job.jobLocationFromList?.length
      ? job.jobLocationFromList[0].label
      : job.jobLocationManually?.length > 0
      ? job.jobLocationManually
      : "";
    setJobLocationName(_jobLocationName);
  }, [job.jobLocationFromList, job.jobLocationManually]);

  useEffect(() => {
    const _jobSeniorityName = !!job.jobSeniorityFromList?.length
      ? job.jobSeniorityFromList[0].label
      : job.jobSeniorityManually?.length > 0
      ? job.jobSeniorityManually
      : "";
    setJobSeniorityName(_jobSeniorityName);
  }, [job.jobSeniorityFromList, job.jobSeniorityManually]);

  useEffect(() => {
    const _jobWorkSetUpName = !!job.jobWorkSetUpFromList?.length
      ? job.jobWorkSetUpFromList[0].label
      : job.jobWorkSetUpManually?.length > 0
      ? job.jobWorkSetUpManually
      : "";
    setJobWorkSetUpName(_jobWorkSetUpName);
  }, [job.jobWorkSetUpFromList, job.jobWorkSetUpManually]);

  const adjustScale = () => {
    let newScale = 1;
    const screenWidth = window.innerWidth;
    if (screenWidth < 750) {
      const targetWidth = screenWidth * 0.9; // 90% de la largeur de l'écran
      newScale = targetWidth / 1080; // Calcul du scale basé sur la largeur initiale de 1080px
    }
    setScale(newScale);
  };

  useEffect(() => {
    adjustScale(); // Initial call to set the scale
    window.addEventListener("resize", adjustScale); // Adjust scale on window resize
    return () => window.removeEventListener("resize", adjustScale); // Cleanup on unmount
  }, []);

  useEffect(() => {
    const adjustFontSize = () => {
      const container = containerRef.current;
      const text = textRef.current;
      const maxFontSize = 127;

      if (!container || !text) return;

      let currentFontSize =
        parseInt(window.getComputedStyle(text).fontSize, 10) || maxFontSize;
      const containerWidth = container.offsetWidth;

      while (text.scrollWidth > containerWidth && currentFontSize > 1) {
        currentFontSize -= 1;
        text.style.fontSize = `${currentFontSize}px`;
      }

      while (
        text.scrollWidth <= containerWidth &&
        currentFontSize < maxFontSize
      ) {
        currentFontSize += 1;
        text.style.fontSize = `${currentFontSize}px`;
        if (text.scrollWidth > containerWidth) {
          currentFontSize -= 1; // Revert last increment
          text.style.fontSize = `${currentFontSize}px`;
          break;
        }
      }
    };

    adjustFontSize();

    // Uncomment if you want dynamic adjustments on resize
    // window.addEventListener("resize", adjustFontSize);
    // return () => {
    //   window.removeEventListener("resize", adjustFontSize);
    // };
  }, [jobFunctionName]);

  return (
    <div className={styles.postCardControlesScrollable}>
      <div className={styles.cardAndControles}>
        <div
          className={styles.viewContainerWrapper}
          style={{
            width: `${scale * 1080}px`,
            height: `${scale * 1080}px`,
            overflow: `${scale < 1 ? "hidden" : "auto"}`,
          }}
        >
          <div
            ref={divRef}
            style={{
              transform: `scale(${scale})`,
            }}
            className={`${styles.viewContainer} ${jobThemeClass}`}
          >
            {!!jobFunctionName?.length && (
              <div className={styles.jobFunction}>
                <div ref={containerRef} className={styles.jobFunctionInner}>
                  <div ref={textRef} className={styles.txtTroncate}>
                    {jobFunctionName}
                  </div>
                </div>
              </div>
            )}

            {!!job?.jobTitle?.length && (
              <div className={styles.jobTitle}>
                <div className={styles.txtTroncate}>{job.jobTitle}</div>
              </div>
            )}
            {!!job?.jobRegion?.length && (
              <div className={styles.jobZone}>{job.jobRegion}</div>
            )}

            {!!img?.image?.url?.length && (
              <>
                <div className={styles.mainImage}>
                  <img
                    className={styles.shadow}
                    src="/img/drop-shadow.png"
                    alt="Shadow"
                  />
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}${img.image.url}`}
                    alt="main"
                    className={styles.image}
                  />
                </div>
                <div className={styles.nowHiring}>
                  <img
                    src={getNowHiringImage(gradient)}
                    alt="Sticker"
                    height="372"
                  />
                </div>
              </>
            )}
            <div className={styles.jobDetailsContainer}>
              <div className={styles.jobDetails}>
                {!!jobSeniorityName?.length && (
                  <div className={styles.jobSeniority}>
                    <div className={styles.jobDetailIcon}>
                      <img
                        src="/img/ICON-SENIORITY.png"
                        alt="worksetup"
                        // width="65"
                        height="71"
                      />
                    </div>
                    <div className={styles.jobDetailName}>
                      {jobSeniorityName}
                    </div>
                  </div>
                )}
                {!!jobWorkSetUpName?.length && (
                  <div className={styles.jobWorkSetUp}>
                    <div className={styles.jobDetailIcon}>
                      <img
                        src="/img/ICON-WORKSETUP.png"
                        alt="worksetup"
                        // width="80"
                        height="80"
                      />
                    </div>
                    <div className={styles.jobDetailName}>
                      {jobWorkSetUpName}
                    </div>
                  </div>
                )}

                {!!jobLocationName?.length && (
                  <div className={styles.jobLocation}>
                    <div className={styles.jobDetailIcon}>
                      <img
                        src="/img/ICON-LOCATION.png"
                        alt="location"
                        // width="50"
                        height="80"
                      />
                    </div>
                    <div className={styles.jobDetailName}>
                      {jobLocationName}
                    </div>
                  </div>
                )}
              </div>

              {/* <div className={styles.BACARDI}>
                <img
                  src="/img/LOGO-BACARDI.png"
                  alt="location"
                  width="424"
                />
              </div> */}
              <div />
            </div>
            {/* <div className={styles.nowHiring2}>
              <div className={styles.now} style={{ color: "#000" }}>
                NOW
              </div>
              <div className={styles.hiring}>HIRING</div>
            </div> */}
            <div className={styles.blackLine}>
              <span className={styles.blackLineKey}>Apply online: </span>
              <span className={styles.blackLineVal}>
                {/* <a href="https://www.bacardilimited.com/careers">
                  bacardilimited.com/careers
                </a> */}
                bacardilimited.com/careers
              </span>
            </div>

            <div className={styles.footer}>
              {/* <FooterSentence slogan={job.slogan} />
               */}
              <div className={styles.footerLogo}>
                <img
                  src="/logo/logo_corporate_black.png"
                  alt="Bat"
                  height="82"
                  // width="auto"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.postCardControlesWrapper}>
        <div className={styles.postCardControles}>
          <LabelButton
            icon={<Icon name="Pdf" size={20} />}
            label={t("downloadPDF")}
            onClick={downloadAsPDF}
            onKeyDown={function noRefCheck() {}}
            variant="secondary"
            size="m"
            corners="squared"
          />
          <LabelButton
            icon={<Icon name="Image" size={20} />}
            label={t("downloadImage")}
            onClick={downloadAsImage}
            onKeyDown={function noRefCheck() {}}
            variant="secondary"
            size="m"
            corners="squared"
          />
        </div>
        <div className={styles.contacts}>
          Contact <a href="mailto:best@bacardi.com">best@bacardi.com</a> for any
          queries or requests.
        </div>
      </div>
    </div>
  );
};
